// === SAA:UI:SHELL-NAVIGATION ===
// Wires the SAA shell policy (utils/shellNavigation) into core's header logo and
// progress stepper through ShellNavigationProvider. Must sit inside the router
// and the WorkflowProvider: the handlers navigate and read workflow state.

import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShellNavigationProvider, useWorkflow } from '@sluitstraat/core';
import type { ProgressStepClick } from '@sluitstraat/core';
import {
  isProgressStepClickable,
  resolveLogoNavigation,
  resolveProgressStepRoute,
  trackProgressArrival,
} from '../utils/shellNavigation';

export const SaaShellNavigation: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { selectedProducts, getStepData, paymentData, data, clearWorkflowData } = useWorkflow();

  // Arrival baseline reads the workflow as it is at the moment of the route change.
  const readsRef = useRef({ selectedProducts, getStepData, paymentData });
  readsRef.current = { selectedProducts, getStepData, paymentData };
  useEffect(() => {
    trackProgressArrival(pathname, readsRef.current);
  }, [pathname]);

  const onLogoClick = useCallback(() => {
    // null = the aanvraag is mid-submit; the click is ignored rather than tearing down state the
    // pending response still writes to.
    const target = resolveLogoNavigation(clearWorkflowData);
    if (target) navigate(target.route, { replace: target.replace });
  }, [navigate, clearWorkflowData]);

  const onProgressStepClick = useCallback(
    (step: ProgressStepClick) => {
      const route = resolveProgressStepRoute(step, { selectedProducts, getStepData, paymentData });
      if (route) navigate(route);
    },
    [navigate, selectedProducts, getStepData, paymentData],
  );

  // Re-created whenever workflow data changes so the stepper re-evaluates forward targets.
  const stepClickable = useCallback(
    (step: ProgressStepClick) => isProgressStepClickable(step, { selectedProducts, getStepData, paymentData }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `data` is read through getStepData; listed so edits re-evaluate the stepper
    [selectedProducts, getStepData, paymentData, data],
  );

  return (
    <ShellNavigationProvider
      onLogoClick={onLogoClick}
      onProgressStepClick={onProgressStepClick}
      isProgressStepClickable={stepClickable}
    >
      {children}
    </ShellNavigationProvider>
  );
};

export default SaaShellNavigation;
